import { type ReactNode } from 'react'

import { composeGroupedDropdownReply } from './composeGroupedDropdownReply'
import { ConversationError } from './ConversationError'
import { CatalogItem } from '../../../../components/CatalogItem'
import { ConfirmationOptions } from '../../../../components/ConfirmationOptions'
import { ConversationText } from '../../../../components/ConversationText'
import {
  GlobalCommandOrgCategoriesForm,
  type IGlobalCommandOrgCategoriesFormPrimaryCategory,
  type IGlobalCommandOrgCategoriesFormValue,
} from '../../../../components/GlobalCommandOrgCategoriesForm'
import { Message } from '../../../../components/Message'
import {
  type ConversationCatalogItemComponent,
  type ConversationChoiceOption,
  type ConversationComponent,
  type ConversationConfirmationOptionsComponent,
  type ConversationGroupedDropdownComponent,
  type ConversationTurnItem,
} from '../../../../value-objects/ConversationTurnItem'
import {
  ConversationComponentTypes,
  ConversationItemKinds,
} from '../../../../value-objects/enums'
import { ConversationCart } from '../ConversationCart'

interface RenderConversationItemOptions {
  isStreaming: boolean
  selectedCatalogItemId: Nullable<string>
  onChoiceSelect: (option: ConversationChoiceOption) => void
  onSendMessage: (message: string) => void
  onCatalogItemSelect: (item: ConversationCatalogItemComponent) => void
}

const isRenderableMessageContent = (content: string): boolean => {
  const trimmed = content.trim()
  return trimmed !== '' && trimmed !== '""'
}

const toPrimaryCategories = (
  component: ConversationGroupedDropdownComponent
): IGlobalCommandOrgCategoriesFormPrimaryCategory[] =>
  component.groups.map((group) => ({
    id: group.id,
    name: group.name,
    secondaryCategories: group.options,
  }))

const isSameId = (a: number | string, b: Nullable<number | string>): boolean =>
  b !== null && String(a) === String(b)

const toInitialValue = (
  component: ConversationGroupedDropdownComponent
): IGlobalCommandOrgCategoriesFormValue | undefined => {
  const group = component.groups.find((candidate) =>
    isSameId(candidate.id, component.defaultGroupId)
  )
  if (!group) return undefined

  const option =
    group.options.find((candidate) =>
      isSameId(candidate.id, component.defaultOptionId)
    ) ?? null

  return {
    primaryCategory: {
      id: group.id,
      name: group.name,
      secondaryCategories: group.options,
    },
    secondaryCategory: option,
  }
}

function renderGroupedDropdown(
  component: ConversationGroupedDropdownComponent,
  key: string,
  disabled: boolean,
  onSendMessage: (message: string) => void
): ReactNode {
  const initialValue = toInitialValue(component)

  const sendReply = (value: IGlobalCommandOrgCategoriesFormValue) => {
    if (!value.primaryCategory) return
    onSendMessage(
      composeGroupedDropdownReply({
        groupLabel: component.groupLabel,
        optionLabel: component.optionLabel,
        group: value.primaryCategory,
        option: value.secondaryCategory,
      })
    )
  }

  // Cancel keeps the original selection: it replies with the values the BE
  // sent as defaults, through the same turn flow as a submit.
  const handleCancel = () => {
    if (!initialValue) return
    sendReply(initialValue)
  }

  return (
    <GlobalCommandOrgCategoriesForm
      key={key}
      primaryCategories={toPrimaryCategories(component)}
      primaryCategoryLabel={component.groupLabel}
      secondaryCategoryLabel={component.optionLabel}
      initialValue={initialValue}
      disabled={disabled}
      onSubmit={sendReply}
      onCancel={handleCancel}
    />
  )
}

interface EnvelopeRenderContext {
  isAnswered: boolean
  disabled: boolean
  selectedCatalogItemId: Nullable<string>
  onChoiceSelect: (option: ConversationChoiceOption) => void
  onSendMessage: (message: string) => void
  onCatalogItemSelect: (item: ConversationCatalogItemComponent) => void
}

function renderConfirmationOptions(
  component: ConversationConfirmationOptionsComponent,
  key: string,
  context: EnvelopeRenderContext
): ReactNode {
  // Quick replies disappear once the envelope is answered; they only
  // stay visible (disabled) while a turn is streaming.
  if (context.isAnswered) return null
  return (
    <ConfirmationOptions
      key={key}
      options={component.options}
      disabled={context.disabled}
      onSelect={context.onChoiceSelect}
    />
  )
}

function renderCatalogItem(
  component: ConversationCatalogItemComponent,
  key: string,
  context: EnvelopeRenderContext
): ReactNode {
  return (
    <CatalogItem
      key={key}
      img={component.img}
      title={component.title}
      body={component.body}
      tags={component.tags}
      price={component.price}
      currency={component.currency}
      vendor={component.vendor}
      rating={component.rating}
      vendorBadges={component.vendorBadges}
      delivery={component.delivery}
      listPrice={component.listPrice}
      isSelected={
        context.selectedCatalogItemId !== null &&
        component.itemId === context.selectedCatalogItemId
      }
      onClick={() => context.onCatalogItemSelect(component)}
    />
  )
}

function renderConversationComponent(
  component: ConversationComponent,
  key: string,
  context: EnvelopeRenderContext
): ReactNode {
  switch (component.type) {
    case ConversationComponentTypes.MESSAGE:
      if (!isRenderableMessageContent(component.content)) return null
      return (
        <Message
          key={key}
          sender={component.sender}
          content={component.content}
        />
      )
    case ConversationComponentTypes.CONFIRMATION_OPTIONS:
      return renderConfirmationOptions(component, key, context)
    case ConversationComponentTypes.GROUPED_DROPDOWN:
      return renderGroupedDropdown(
        component,
        key,
        context.disabled,
        context.onSendMessage
      )
    case ConversationComponentTypes.CART:
      return <ConversationCart key={key} cart={component} />
    case ConversationComponentTypes.CATALOG_ITEM:
      return renderCatalogItem(component, key, context)
    case ConversationComponentTypes.TEXT:
      return (
        <ConversationText
          key={key}
          variant={component.variant}
          content={component.content}
        />
      )
    default:
      return null
  }
}

export const renderConversationItem = (
  item: ConversationTurnItem,
  {
    isStreaming,
    selectedCatalogItemId,
    onChoiceSelect,
    onSendMessage,
    onCatalogItemSelect,
  }: RenderConversationItemOptions
): ReactNode => {
  switch (item.kind) {
    case ConversationItemKinds.MESSAGE:
      return <Message sender={item.sender} content={item.content} />
    case ConversationItemKinds.ENVELOPE: {
      const context: EnvelopeRenderContext = {
        isAnswered: item.isAnswered,
        disabled: item.isAnswered || isStreaming,
        selectedCatalogItemId,
        onChoiceSelect,
        onSendMessage,
        onCatalogItemSelect,
      }
      return item.components.map((component, index) =>
        renderConversationComponent(
          component,
          `${item.envelopeId}_${component.type}_${index}`,
          context
        )
      )
    }
    case ConversationItemKinds.ERROR:
      return <ConversationError />
    default:
      return null
  }
}
